import { PlusOutlined, ExclamationCircleOutlined, SettingTwoTone, WifiOutlined, InfoCircleTwoTone } from "@ant-design/icons";
import { Button, Card, Col, Empty, Row, Spin, Tag } from "antd";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import PalletDeviceService from "../../../../services/PalletDeviceService";
import PalletDevice, { PalletDeviceConnections } from "../../../../models/PalletDevice";
import HardwareInfo from "./subcomponents/HardwareInfo";
import { WarehouseSummary } from "../../../../models/Warehouse";
import WarehouseService from "../../../../services/WarehouseService";
import DeviceCreateModal from "./DeviceCreateModal";
import DeviceModifyModal from "./DeviceModifyModal";
import ResizeObserver from 'resize-observer-polyfill';
import { Link, useNavigate } from "react-router-dom";
import UserContext from "../../../../context/UserContext";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import { useLanguage } from "../../../../context/LanguageContext";

const HARDWARE_INFO_TIMEOUT_MS = 8000;
const actionIconStyle = { fontSize: 22, cursor: "pointer" };
const disabledActionIconStyle = { fontSize: 22, color: "#bfbfbf", cursor: "not-allowed" };

const Devices: React.FC = () => {
  const [palletDevices, setPalletDevices] = useState(new Array<PalletDevice>());
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModifyModalOpen, setIsModifyModalOpen] = useState(false);
  const [warehouses, setWarehouses] = useState<WarehouseSummary[]>([]);
  const [warehouseIdx, setWarehouseIdx] = useState<number | undefined>(-1);
  const [selectedPalletDevice, setSelectedPalletDevice] = useState<PalletDevice | undefined>(undefined);
  const [devicesConnections, setDevicesConnections] = useState<PalletDeviceConnections[]>([]);
  const [parentSizes, setParentSizes] = useState<Record<number, { width: number, height: number }>>({});
  const [cardsWithoutInfo, setCardsWithoutInfo] = useState<Record<number, boolean>>({});
  const infoTimeoutsRef = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
  const isMobile = useContext(UserContext)
  const navigate = useNavigate()
  const { language } = useLanguage();

  const screens = useBreakpoint();


  const updateContent = useCallback(() => {
    PalletDeviceService.getAll().then((palletDevices) => {
      if (palletDevices == null) {
        return;
      }
      setPalletDevices(palletDevices);
      WarehouseService.getAll(navigate).then((warehouses) => {
        if (warehouses == null) {
          return;
        }
        const no_warehouse = new WarehouseSummary();
        no_warehouse.name = "No Warehouse";
        no_warehouse.idx = -1;
        setWarehouses([...warehouses, no_warehouse]);
        setLoading(false);
      });
    });
    PalletDeviceService.getConnections().then((connections) => {
      if (connections == null) {
        return;
      }
      setDevicesConnections(connections);
    });
  }, [navigate]);

  useEffect(() => {
    updateContent();
    const interval = setInterval(() => {
      updateContent();
    }, 50000);
    return () => clearInterval(interval);
  }, [updateContent]);

  const handleResize = (idx: number, entry: ResizeObserverEntry) => {
    setParentSizes((prevSizes) => ({
      ...prevSizes,
      [idx]: {
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      },
    }));
  };

  const showModal = () => {
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
  };

  const showModifyModal = (palletDevice: PalletDevice) => {
    setSelectedPalletDevice(palletDevice);
    setIsModifyModalOpen(true);
  };
  const closeModifyModal = () => {
    setIsModifyModalOpen(false);
  };

  const handleWarehouseChange = (value: number) => {
    setWarehouseIdx(value);
  };

  const handleInformationStateChange = useCallback((deviceIdx: number, hasInformation: boolean) => {
    if (hasInformation) {
      if (infoTimeoutsRef.current[deviceIdx]) {
        clearTimeout(infoTimeoutsRef.current[deviceIdx]);
        delete infoTimeoutsRef.current[deviceIdx];
      }

      setCardsWithoutInfo((previousCardsWithoutInfo) => {
        if (!previousCardsWithoutInfo[deviceIdx]) {
          return previousCardsWithoutInfo;
        }
        const nextCardsWithoutInfo = { ...previousCardsWithoutInfo };
        delete nextCardsWithoutInfo[deviceIdx];
        return nextCardsWithoutInfo;
      });
      return;
    }

    if (infoTimeoutsRef.current[deviceIdx]) {
      return;
    }

    infoTimeoutsRef.current[deviceIdx] = setTimeout(() => {
      setCardsWithoutInfo((previousCardsWithoutInfo) => ({
        ...previousCardsWithoutInfo,
        [deviceIdx]: true,
      }));
      delete infoTimeoutsRef.current[deviceIdx];
    }, HARDWARE_INFO_TIMEOUT_MS);
  }, []);

  useEffect(() => {
    return () => {
      Object.values(infoTimeoutsRef.current).forEach((timeoutId) => clearTimeout(timeoutId));
    };
  }, []);

  return (
    <>
      <Spin spinning={loading}>
        <Card
          title={"Pallet Devices"}
          extra={
            <>
              <Button
                type="primary"
                onClick={showModal}
                icon={<PlusOutlined />}
                shape="round"
                size={window.innerWidth < 900 ? "small" : "middle"} // Responsive button size
              >
                {language === "es" ? "Agregar" : "Add"}
              </Button>
              <DeviceCreateModal
                onClose={closeModal}
                onUpdateContent={updateContent}
                isLoading={loading}
                warehouseIdx={warehouseIdx}
                onSetWarehouseIdx={setWarehouseIdx}
                warehouses={warehouses}
                palletDevices={palletDevices}
                isOpen={isModalOpen}
                onWarehouseChange={handleWarehouseChange}
              />
            </>
          }
          style={{
            margin: isMobile ? '10px' : '20px',
            padding: isMobile ? '10px' : '20px',
          }}
        >
          <Row gutter={[16, 16]} style={{ overflow: "auto" }}>
            {palletDevices.map((palletDevice, i) => (
              <Col key={i} xs={24} md={12} lg={8} style={{ overflow: "auto" }}> {/* Responsive columns */}
                {/** Disable actions when a device has no hardware information after timeout. */}
                {(() => {
                  const isCardWithoutInfo = cardsWithoutInfo[Number(palletDevice.idx)];
                  return (
                <Card
                  headStyle={{ position: "relative", zIndex: 1 }}
                  bodyStyle={{ position: "relative", zIndex: 1 }}
                  title={
                    <div style={{ fontSize: screens.lg ? "0.90vw" : screens.md ? "1.2vw" : screens.sm ? "1.5vw" : "3vw"}}> {/* Responsive font size */}
                      {palletDevice.name}{" "}
                      {devicesConnections.find((device) => device.idx === palletDevice.idx)?.is_connected ? (
                        <Tag style={{ left: "5px", fontSize: window.innerWidth < 900 ? "2.4vw" : "0.8vw", paddingInline: "6px", lineHeight: "18px" }} icon={<WifiOutlined />} color="green">
                          {language === "es" ? "En linea" : "online"}
                        </Tag>
                      ) : (
                        <>
                          <Tag style={{ left: "5px", fontSize: window.innerWidth < 900 ? "2.4vw" : "0.8vw", paddingInline: "6px", lineHeight: "18px" }} icon={<ExclamationCircleOutlined />} color="red">
                            {language === "es" ? "Fuera de linea" : "offline"}
                          </Tag>
                          <div style={{ fontSize: window.innerWidth < 900 ? "2.3vw" : "72%", color: "#7a848d" }}>
                            {language === "es" ? "Desde" : "since"} {devicesConnections.find((device) => device.idx === palletDevice.idx)?.timestamp}
                          </div>
                        </>
                      )}
                    </div>
                  }
                  ref={(el) => {
                    if (el) {
                      const observer = new ResizeObserver((entries) => {
                        for (let entry of entries) {
                          if (entry.target === el) {
                            handleResize(i, entry);
                          }
                        }
                      });
                      observer.observe(el);
                    }
                  }}
                  style={{
                    minHeight: "270px",
                    overflow: "auto",
                    position: "relative",
                    filter: isCardWithoutInfo ? "grayscale(100%)" : "none",
                    opacity: isCardWithoutInfo ? 0.7 : 1,
                    userSelect: isCardWithoutInfo ? "none" : "auto",
                  }}
                  extra={<>
                    {isCardWithoutInfo ? (
                      <InfoCircleTwoTone
                        style={{ ...disabledActionIconStyle, marginRight: "12px" }}
                        twoToneColor="#bfbfbf"
                      />
                    ) : (
                      <Link to={`${palletDevice.idx}`} style={{ marginRight: "12px" }}>
                        <InfoCircleTwoTone style={actionIconStyle} twoToneColor="#1677ff" />
                      </Link>
                    )}

                    <SettingTwoTone
                      style={isCardWithoutInfo ? disabledActionIconStyle : actionIconStyle}
                      twoToneColor={isCardWithoutInfo ? "#bfbfbf" : "#1677ff"}
                      onClick={isCardWithoutInfo ? undefined : () => showModifyModal(palletDevice)}
                    />
                  </>}
                >
                  {palletDevice.idx ? (
                    <>
                      <HardwareInfo
                        parentWidth={parentSizes[i]?.width || 0}
                        parentHeight={parentSizes[i]?.height || 0}
                        deviceIdx={palletDevice.idx}
                        onInformationStateChange={handleInformationStateChange}
                        isTimedOut={isCardWithoutInfo}
                      />
                    </>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  )}
                </Card>
                  );
                })()}
              </Col>
            ))}
          </Row>
        </Card>
        <DeviceModifyModal
          onClose={closeModifyModal}
          onUpdateContent={updateContent}
          isLoading={loading}
          palletDevice={selectedPalletDevice}
          warehouses={warehouses}
          palletDevices={palletDevices}
          isOpen={isModifyModalOpen}
        />
      </Spin>
    </>
  );
};

export default Devices;
